import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
    PiArrowLeft,
    PiArrowRight,
    PiBoat,
    PiMagnifyingGlass,
    PiBell,
    PiCheckCircle,
    PiClockCountdown,
    PiEnvelopeSimple,
    PiLockKey,
    PiPackage,
    PiTrophy,
    PiWarehouse,
    PiWarningCircle,
    PiX,
    PiXCircle,
} from 'react-icons/pi';
import {
    Badge,
    Button,
    LoadingState,
    PageHeader,
    Text,
    Title,
} from '@v2/design-system';
import V2Modal from '@v2/components/ui/V2Modal';
import api from '@v2/lib/api';
import { hasRole } from '@v2/lib/auth';
import { useI18n } from '@v2/providers/I18nProvider';
import { ploomesDealPath } from '@v2/features/ploomes-deals/dealPath';
import ReservationExpiryCountdown from '@v2/features/stock-reservations/ReservationExpiryCountdown';
import {
    StockPhaseBadge,
    StockShipmentLink,
    phaseFromSource,
} from '@v2/features/products/StockPhaseBadge';
import { cn } from '@v2/utils/cn';
import { DOT_STYLE, dotInlineStyle } from '@v2/features/colors/ColorDot';
import { useState, type ComponentType } from 'react';

type StockRequestDetail = {
    id: number;
    status: string;
    source?: 'cd' | 'shipment';
    source_shipment_id?: number | null;
    source_shipment?: {
        id: number;
        label: string;
        stage_label: string;
        client?: string | null;
    } | null;
    qty_requested: number;
    qty_approved?: number | null;
    reason: string | null;
    decision_note: string | null;
    decided_at?: string | null;
    decided_by?: { id: number; name: string; avatar_url?: string | null } | null;
    color_swatches?: Array<{ slot: string; label: string; name: string | null; hex: string | null }>;
    qty_from_stock_effective?: number;
    qty_to_import_effective?: number;
    requested_at: string | null;
    reserved_at?: string | null;
    expires_at: string | null;
    deal_won?: boolean;
    requested_by: {
        id: number;
        name: string;
        email?: string | null;
        avatar_url?: string | null;
    } | null;
    deal: {
        id: number;
        title: string;
        internal_number: string | null;
        deal_number: string | null;
        owner_name: string | null;
        contact_name?: string | null;
        url_key?: string;
    } | null;
    order_product: {
        id: number;
        product_name: string;
        product_code: string | null;
        quantity: number;
    } | null;
    product: {
        id: number;
        code: string | null;
        name: string;
        image_url?: string | null;
    } | null;
};

type StockRequestEvent = {
    id: number;
    action: string;
    created_at: string | null;
    actor: { id: number; name: string } | null;
};

const AVATAR_FALLBACK = '/img/favicon.png';

function statusTone(status: string): 'warning' | 'success' | 'danger' | 'secondary' | 'info' {
    if (status === 'reserved' || status === 'pending' || status === 'approved') return 'warning';
    if (status === 'fulfilled') return 'success';
    if (status === 'cancelled' || status === 'rejected') return 'danger';
    if (status === 'expired') return 'secondary';
    return 'info';
}

function formatDateTime(value: string | null): string {
    if (!value) return '—';
    try {
        return new Intl.DateTimeFormat('pt-BR', {
            dateStyle: 'short',
            timeStyle: 'short',
        }).format(new Date(value));
    } catch {
        return value;
    }
}

function canCancel(row: StockRequestDetail): boolean {
    if (row.status !== 'reserved') return false;
    const userId = window.__V2__?.user?.id;
    const isRequester = userId != null && row.requested_by?.id === userId;
    return (
        isRequester ||
        hasRole('commercial_director') ||
        hasRole('admin') ||
        hasRole('super_admin')
    );
}

type TranslateFn = (key: string, params?: Record<string, string | number>) => string;

function eventLabel(action: string, t: TranslateFn): string {
    const reminderMatch = /^reminder_(\d+)h$/.exec(action);
    if (reminderMatch) {
        return t('v2.stock_approvals.event.reminder', { hours: reminderMatch[1] });
    }

    const key = `v2.stock_approvals.event.${action}`;
    const translated = t(key);
    return translated === key ? action : translated;
}

function eventVisual(action: string): {
    Icon: ComponentType<{ className?: string }>;
    tone: string;
} {
    if (/^reminder_\d+h$/.test(action)) {
        return { Icon: PiBell, tone: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300' };
    }

    switch (action) {
        case 'reserved':
            return { Icon: PiLockKey, tone: 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300' };
        case 'email_created_sent':
            return {
                Icon: PiEnvelopeSimple,
                tone: 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300',
            };
        case 'cd_hold_applied':
            return {
                Icon: PiWarehouse,
                tone: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
            };
        case 'cancelled':
            return { Icon: PiXCircle, tone: 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300' };
        case 'fulfilled':
            return {
                Icon: PiCheckCircle,
                tone: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
            };
        case 'expired_no_won_deal':
            return {
                Icon: PiClockCountdown,
                tone: 'bg-gray-100 text-gray-600 dark:bg-zinc-700 dark:text-zinc-300',
            };
        case 'no_seller_action':
            return {
                Icon: PiWarningCircle,
                tone: 'bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300',
            };
        default:
            return { Icon: PiPackage, tone: 'bg-gray-100 text-gray-600 dark:bg-zinc-700 dark:text-zinc-300' };
    }
}

export default function StockReservationDetailPage() {
    const { id } = useParams<{ id: string }>();
    const { t } = useI18n();
    const navigate = useNavigate();
    const qc = useQueryClient();
    const [confirmCancel, setConfirmCancel] = useState(false);
    const [cancelNote, setCancelNote] = useState('');

    const detailQuery = useQuery({
        queryKey: ['stock-request', id],
        queryFn: async () =>
            (await api.get<{ data: StockRequestDetail }>(`/stock-requests/${id}`)).data.data,
        enabled: Boolean(id),
    });

    const eventsQuery = useQuery({
        queryKey: ['stock-request-events', id],
        queryFn: async () =>
            (await api.get<{ data: StockRequestEvent[] }>(`/stock-requests/${id}/events`)).data
                .data,
        enabled: Boolean(id),
    });

    const cancelMut = useMutation({
        mutationFn: async () => {
            const { data } = await api.post(`/stock-requests/${id}/cancel`, { note: cancelNote.trim() || null });
            return data;
        },
        onSuccess: (data) => {
            toast.success(data?.message ?? t('v2.stock_approvals.cancel_success'));
            setConfirmCancel(false);
            void qc.invalidateQueries({ queryKey: ['stock-request', id] });
            void qc.invalidateQueries({ queryKey: ['stock-requests'] });
            void qc.invalidateQueries({ queryKey: ['meta', 'navigation'] });
        },
        onError: (err: { response?: { data?: { message?: string } } }) => {
            toast.error(err?.response?.data?.message ?? t('v2.stock_approvals.cancel_error'));
        },
    });

    const row = detailQuery.data;
    const isActive =
        row?.status === 'reserved' || row?.status === 'pending' || row?.status === 'approved';

    const statusLabel = (value: string) => {
        const key = `v2.stock_approvals.status_${value}`;
        const translated = t(key);
        return translated === key ? value : translated;
    };

    if (detailQuery.isLoading) {
        return <LoadingState variant="detail" />;
    }

    if (detailQuery.isError || !row) {
        return (
            <div>
                <PageHeader
                    title={t('v2.stock_approvals.detail_title')}
                    breadcrumb={[
                        { name: t('v2.common.dashboard'), href: '/' },
                        { name: t('v2.stock_approvals.title'), href: '/estoque/aprovacoes' },
                        { name: t('v2.stock_approvals.detail_title') },
                    ]}
                />
                <p className="text-sm text-rose-600">{t('v2.stock_approvals.detail_error')}</p>
                <Button variant="outline" className="mt-4" onClick={() => navigate('/estoque/aprovacoes')}>
                    <PiArrowLeft className="me-1.5 size-4" />
                    {t('v2.stock_approvals.back_to_list')}
                </Button>
            </div>
        );
    }

    const dealLabel =
        row.deal?.internal_number || row.deal?.deal_number || (row.deal ? `#${row.deal.id}` : '—');
    const fromProcess = row.source === 'shipment';
    const D = 'v2.stock_approvals.detail';
    const lineQty = row.order_product?.quantity ?? row.qty_requested;
    const reservedQty = row.qty_approved ?? row.qty_requested;
    const fromStock = row.qty_from_stock_effective ?? (isActive || row.status === 'fulfilled' ? reservedQty : 0);
    const toImport = row.qty_to_import_effective ?? Math.max(0, lineQty - fromStock);
    const swatches = (row.color_swatches ?? []).filter((sw) => sw.name);
    const shipmentId = row.source_shipment?.id ?? row.source_shipment_id ?? null;
    const sku = row.product?.code ?? row.order_product?.product_code ?? null;
    const nextStep = row.status === 'fulfilled'
        ? t(`${D}.next_fulfilled`)
        : !isActive
          ? t(`${D}.next_closed`)
          : row.deal_won
            ? t(`${D}.next_won`)
            : t(`${D}.next_active`, { date: formatDateTime(row.expires_at) });

    return (
        <div>
            <PageHeader
                title={t(`${D}.title`, { id: row.id })}
                breadcrumb={[
                    { name: t('v2.common.dashboard'), href: '/' },
                    { name: t('v2.stock_approvals.title'), href: '/estoque/aprovacoes' },
                    { name: `#${row.id}` },
                ]}
            >
                <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={() => navigate('/estoque/aprovacoes')}>
                        <PiArrowLeft className="me-1.5 size-4" />
                        {t('v2.stock_approvals.back_to_list')}
                    </Button>
                    {canCancel(row) && (
                        <Button size="sm" className="bg-rose-600 text-white hover:bg-rose-700" onClick={() => setConfirmCancel(true)}>
                            <PiX className="me-1.5 size-4" />
                            {t('v2.stock_approvals.cancel')}
                        </Button>
                    )}
                </div>
            </PageHeader>

            <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
                <div className="min-w-0 space-y-4">
                    {/* Produto + situação */}
                    <article className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
                        <div className="flex flex-col gap-5 sm:flex-row">
                            <div className="flex size-32 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-gray-100 bg-white dark:border-zinc-700 dark:bg-zinc-800">
                                {row.product?.image_url ? (
                                    <img src={row.product.image_url} alt="" className="size-full object-contain p-2" />
                                ) : (
                                    <PiPackage className="size-12 text-gray-300" />
                                )}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                    <Badge color={statusTone(row.status)} variant="flat">{statusLabel(row.status)}</Badge>
                                    {row.deal_won && (
                                        <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-700 dark:bg-violet-500/15 dark:text-violet-300">
                                            <PiTrophy className="size-3.5" />
                                            {t('v2.stock_approvals.deal_won')}
                                        </span>
                                    )}
                                </div>
                                <Title as="h2" className="mt-2 text-xl">
                                    {row.order_product?.product_name ?? t('v2.stock_approvals.product_fallback')}
                                </Title>
                                {sku && <p className="mt-0.5 font-mono text-xs text-gray-400">{sku}</p>}

                                {/* Origem numa linha só: de onde sai a unidade */}
                                <div className="mt-3 inline-flex flex-wrap items-center gap-2 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-800/60">
                                    {fromProcess ? <PiBoat className="size-4 text-sky-600" /> : <PiWarehouse className="size-4 text-emerald-600" />}
                                    <span className="text-gray-500">{t(`${D}.origin`)}:</span>
                                    {fromProcess ? (
                                        <>
                                            <StockShipmentLink shipmentId={shipmentId} label={row.source_shipment?.label} />
                                            <StockPhaseBadge phase={phaseFromSource(row.source)} />
                                            {row.source_shipment?.stage_label && (
                                                <span className="text-xs text-gray-500">{row.source_shipment.stage_label}</span>
                                            )}
                                        </>
                                    ) : (
                                        <span className="font-semibold text-gray-800 dark:text-zinc-100">{t('v2.stock_approvals.source_cd')}</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                            <InfoTile label={t(`${D}.sale`)}>
                                {row.deal ? (
                                    <Link to={ploomesDealPath(row.deal)} className="font-semibold text-sky-600 hover:underline">
                                        #{dealLabel}
                                    </Link>
                                ) : '—'}
                                <p className="truncate text-xs text-gray-500" title={row.deal?.title}>{row.deal?.title}</p>
                            </InfoTile>
                            <InfoTile label={t(`${D}.client_seller`)}>
                                <p className="truncate font-semibold text-gray-800 dark:text-zinc-100" title={row.deal?.contact_name ?? ''}>
                                    {row.deal?.contact_name || '—'}
                                </p>
                                <p className="truncate text-xs text-gray-500">{row.deal?.owner_name || '—'}</p>
                            </InfoTile>
                            <InfoTile label={t(`${D}.quantity`)}>
                                <p className="font-semibold text-gray-800 dark:text-zinc-100">
                                    <span className="text-xl tabular-nums">{reservedQty}</span>
                                    <span className="ms-1 text-xs font-normal text-gray-500">{t(`${D}.of_line`, { total: lineQty })}</span>
                                </p>
                                <div className="mt-1.5 flex h-1.5 overflow-hidden rounded-full bg-sky-100 dark:bg-sky-950/50">
                                    <div className="bg-emerald-500" style={{ width: `${lineQty > 0 ? (fromStock / lineQty) * 100 : 0}%` }} />
                                </div>
                                <p className="mt-1 flex justify-between text-[11px]">
                                    <span className="text-emerald-700 dark:text-emerald-300">{t(`${D}.from_stock`, { qty: fromStock })}</span>
                                    <span className="text-sky-700 dark:text-sky-300">{t(`${D}.to_import`, { qty: toImport })}</span>
                                </p>
                            </InfoTile>
                            <InfoTile label={t(`${D}.color`)}>
                                {swatches.length > 0 ? (
                                    <div className="flex items-center gap-2" title={swatches.map((sw) => `${sw.label}: ${sw.name}`).join('\n')}>
                                        <span className="flex -space-x-1">
                                            {swatches.map((sw) => (
                                                <span
                                                    key={sw.slot}
                                                    className={cn('size-5 rounded-full border-2 border-white dark:border-zinc-900', !sw.hex && DOT_STYLE.off)}
                                                    style={dotInlineStyle(sw.hex ? 'standard' : 'off', sw.hex)}
                                                />
                                            ))}
                                        </span>
                                        <span className="truncate text-xs font-medium text-gray-700 dark:text-zinc-200">{swatches[0]?.name}</span>
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-500">{t(`${D}.any_color`)}</p>
                                )}
                            </InfoTile>
                        </div>

                        {/* Motivo com quem e quando */}
                        <div className="mt-4 flex gap-3 rounded-xl border border-amber-100 bg-amber-50/70 p-3 dark:border-amber-900/40 dark:bg-amber-500/10">
                            <img src={row.requested_by?.avatar_url || AVATAR_FALLBACK} alt="" className="size-9 shrink-0 rounded-full object-cover" />
                            <div className="min-w-0">
                                <p className="text-xs text-amber-900/70 dark:text-amber-200/70">
                                    {t(`${D}.reserved_by`, {
                                        name: row.requested_by?.name ?? '—',
                                        date: formatDateTime(row.reserved_at ?? row.requested_at),
                                    })}
                                </p>
                                <p className="mt-0.5 text-sm text-amber-950 dark:text-amber-100">
                                    {row.reason ? `“${row.reason}”` : t(`${D}.no_reason`)}
                                </p>
                            </div>
                        </div>

                        {(row.decision_note || row.decided_by) && (
                            <div className="mt-3 flex gap-3 rounded-xl border border-gray-100 bg-gray-50 p-3 dark:border-zinc-700 dark:bg-zinc-800/60">
                                <img src={row.decided_by?.avatar_url || AVATAR_FALLBACK} alt="" className="size-9 shrink-0 rounded-full object-cover" />
                                <div className="min-w-0">
                                    <p className="text-xs text-gray-500">
                                        {t(`${D}.decided_by`, { name: row.decided_by?.name ?? '—', date: formatDateTime(row.decided_at ?? null) })}
                                    </p>
                                    {row.decision_note && <p className="mt-0.5 text-sm text-gray-800 dark:text-zinc-100">“{row.decision_note}”</p>}
                                </div>
                            </div>
                        )}
                    </article>

                    <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
                        <Title as="h3" className="mb-4 text-base">{t('v2.stock_approvals.events')}</Title>
                        {eventsQuery.isLoading && <LoadingState variant="inline" />}
                        {eventsQuery.isError && <Text className="text-sm text-rose-600">{t('v2.stock_approvals.events_error')}</Text>}
                        {(eventsQuery.data ?? []).length === 0 && !eventsQuery.isLoading && (
                            <Text className="text-sm text-gray-400">{t('v2.stock_approvals.events_empty')}</Text>
                        )}
                        {(eventsQuery.data ?? []).length > 0 && (
                            <ol className="relative space-y-0">
                                {(eventsQuery.data ?? []).map((ev, idx, list) => {
                                    const isLast = idx === list.length - 1;
                                    const { Icon, tone } = eventVisual(ev.action);

                                    return (
                                        <li key={ev.id} className="relative flex gap-3">
                                            <div className="flex flex-col items-center">
                                                <span className={cn('relative z-10 flex size-9 shrink-0 items-center justify-center rounded-full', tone)}>
                                                    <Icon className="size-4" />
                                                </span>
                                                {!isLast && <span aria-hidden className="mt-1 w-px flex-1 min-h-4 bg-gray-200 dark:bg-zinc-700" />}
                                            </div>
                                            <div className={cn('min-w-0 flex-1', !isLast && 'pb-5')}>
                                                <p className="text-sm font-semibold text-gray-900 dark:text-zinc-100">{eventLabel(ev.action, t)}</p>
                                                <p className="mt-0.5 text-xs text-gray-500 dark:text-zinc-400">
                                                    {formatDateTime(ev.created_at)}
                                                    {ev.actor?.name ? ` · ${ev.actor.name}` : ''}
                                                </p>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ol>
                        )}
                    </section>
                </div>

                <aside className="space-y-4">
                    <ReservationExpiryCountdown
                        reservedAt={row.reserved_at ?? row.requested_at}
                        expiresAt={row.expires_at}
                        active={Boolean(isActive)}
                        size="lg"
                    />

                    <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t(`${D}.next_title`)}</p>
                        <p className="mt-1.5 text-sm text-gray-700 dark:text-zinc-200">{nextStep}</p>
                    </section>

                    <section className="rounded-2xl border border-gray-200 bg-white p-2 shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
                        {row.deal && <QuickLink to={ploomesDealPath(row.deal)} icon={PiTrophy} label={t(`${D}.open_sale`, { number: dealLabel })} />}
                        {fromProcess && shipmentId && (
                            <QuickLink to={`/shipments/${shipmentId}`} icon={PiBoat} label={t(`${D}.open_process`, { label: row.source_shipment?.label ?? `#${shipmentId}` })} />
                        )}
                        {sku && <QuickLink to={`/estoque/consulta/${encodeURIComponent(sku)}`} icon={PiMagnifyingGlass} label={t(`${D}.open_stock`)} />}
                    </section>
                </aside>
            </div>

            <V2Modal
                open={confirmCancel}
                onClose={() => setConfirmCancel(false)}
                title={t('v2.stock_approvals.cancel_title')}
                size="md"
                footer={
                    <>
                        <Button variant="outline" onClick={() => setConfirmCancel(false)}>
                            {t('v2.common.cancel')}
                        </Button>
                        <Button
                            className="bg-rose-600 text-white hover:bg-rose-700"
                            isLoading={cancelMut.isPending}
                            onClick={() => cancelMut.mutate()}
                        >
                            {t('v2.stock_approvals.cancel_confirm')}
                        </Button>
                    </>
                }
            >
                <div className="space-y-3">
                    <Text className="text-sm text-gray-600 dark:text-zinc-300">
                        {t('v2.stock_approvals.cancel_body', {
                            qty: String(row.qty_requested),
                            product: row.order_product?.product_name ?? '',
                        })}
                    </Text>
                    <label className="block">
                        <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-zinc-200">{t('v2.stock_approvals.cancel_note')}</span>
                        <textarea
                            value={cancelNote}
                            onChange={(e) => setCancelNote(e.target.value)}
                            rows={2}
                            placeholder={t('v2.stock_approvals.cancel_note_ph')}
                            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100"
                        />
                    </label>
                </div>
            </V2Modal>
        </div>
    );
}

function InfoTile({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="min-w-0 rounded-xl bg-gray-50 px-3 py-2.5 dark:bg-zinc-800/70">
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">{label}</p>
            {children}
        </div>
    );
}

function QuickLink({ to, icon: Icon, label }: { to: string; icon: ComponentType<{ className?: string }>; label: string }) {
    return (
        <Link
            to={to}
            className="flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:text-zinc-200 dark:hover:bg-zinc-800"
        >
            <Icon className="size-4 shrink-0 text-gray-400" />
            <span className="min-w-0 flex-1 truncate">{label}</span>
            <PiArrowRight className="size-3.5 shrink-0 text-gray-300" />
        </Link>
    );
}
